/**
 * External dependencies
 */
import { PAGE_RATIO } from '@web-stories-wp/units';
import { AssetTypes, getAssetRatio } from '@src/hooks/studioContext/CMSParams';
/**
 * Internal dependencies
 */
import cardAsset16x9Overlay from './assets/16_9/16_9-card.png';
import cardAsset1x1Overlay from './assets/1_1/1_1-card.png';
import storyIconOverlay from './assets/1_1/1_1-story-thumbnail.png';
import cardAsset2x3Overlay from './assets/2_3/2_3-card.png';
import storyThumbnailChipLeftOverlay from './assets/2_3/2_3-thumbnail-chip-left.png';
import storyThumbnailOverlay from './assets/2_3/2_3-thumbnail-chip-right.png';
import cardAsset4x5Overlay from './assets/4_5/4_5-card.png';
import clipAdMobileOverlay from './assets/9_16/mobile/9_16-clip-ad-mobile.png';
import clipMobileOverlay from './assets/9_16/mobile/9_16-clip-mobile.png';
import adMobileOverlay from './assets/9_16/mobile/9_16-story-ad-mobile.png';
import storyMobileOverlay from './assets/9_16/mobile/9_16-story-mobile.png';
import cardAsset9x16Overlay from './assets/9_16/9_16-card.png';
import clipAdCombinedOverlay from './assets/9_16/9_16-clip-ad-combined.png';
import clipCombinedOverlay from './assets/9_16/9_16-clip-combined.png';
import adCombinedOverlay from './assets/9_16/9_16-story-ad-combined.png';
import storyCombinedOverlay from './assets/9_16/9_16-story-combined.png';
import clipAdWebOverlay from './assets/9_16/web/9_16-clip-ad-web.png';
import clipWebOverlay from './assets/9_16/web/9_16-clip-web.png';
import adWebOverlay from './assets/9_16/web/9_16-story-ad-web.png';
import storyWebOverlay from './assets/9_16/web/9_16-story-web.png';

export const NOT_SET_SAFE_ZONE_PRESET_ID = '__not_set__';

const SAFE_ZONE_LABELS_BY_ASSET_TYPE = {
  [AssetTypes.Story]: 'Story',
  [AssetTypes.StoryThumbnail]: 'Story Thumbnail',
  [AssetTypes.StoryIcon]: 'Story Icon',
  [AssetTypes.Clip]: 'Clip',
  [AssetTypes.ClipThumbnail]: 'Clip Thumbnail',
  [AssetTypes.Ad]: 'Story Ad',
};

const SAFE_ZONE_LABEL_SUFFIX_BY_ASSET_TYPE = {
  [AssetTypes.Story]: ' (Mobile)',
  [AssetTypes.Clip]: ' (Mobile)',
  [AssetTypes.Ad]: ' (Mobile)',
};

const SAFE_ZONE_OVERLAY_BY_ASSET_TYPE = {
  [AssetTypes.Story]: storyMobileOverlay,
  [AssetTypes.StoryThumbnail]: storyThumbnailOverlay,
  [AssetTypes.StoryIcon]: storyIconOverlay,
  [AssetTypes.Clip]: clipMobileOverlay,
  [AssetTypes.ClipThumbnail]: storyThumbnailOverlay,
  [AssetTypes.Ad]: adMobileOverlay,
};

const RATIO_PRECISION = 6;

function getRatioKey(ratio) {
  if (!ratio) {
    return null;
  }

  return ratio.toFixed(RATIO_PRECISION);
}

function getRatioKeyForAssetType(assetType) {
  return getRatioKey(getAssetRatio(assetType));
}

function getSafeZoneGeometryForRatio(ratio) {
  if (!ratio || !PAGE_RATIO) {
    return {
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    };
  }

  if (ratio > PAGE_RATIO) {
    const height = PAGE_RATIO / ratio;

    return {
      x: 0,
      y: (1 - height) / 2,
      width: 1,
      height,
    };
  }

  if (ratio < PAGE_RATIO) {
    const width = ratio / PAGE_RATIO;

    return {
      x: (1 - width) / 2,
      y: 0,
      width,
      height: 1,
    };
  }

  return {
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  };
}

function getSafeZoneGeometryForAssetType(assetType) {
  return getSafeZoneGeometryForRatio(getAssetRatio(assetType));
}

function getPresetIdForAssetType(assetType) {
  return `safe-zone-${assetType}`;
}

const PRESET_ID_BY_ASSET_TYPE = Object.values(AssetTypes).reduce(
  (map, assetType) => {
    map[assetType] = getPresetIdForAssetType(assetType);
    return map;
  },
  {}
);

const BASE_SAFE_ZONE_PRESETS = Object.keys(SAFE_ZONE_OVERLAY_BY_ASSET_TYPE).reduce(
  (presets, assetType) => {
    const presetId = PRESET_ID_BY_ASSET_TYPE[assetType];
    const label = SAFE_ZONE_LABELS_BY_ASSET_TYPE[assetType] || assetType;
    const suffix = SAFE_ZONE_LABEL_SUFFIX_BY_ASSET_TYPE[assetType] || '';
    const ratio = getAssetRatio(assetType);

    presets[presetId] = {
      id: presetId,
      label: `${label}${suffix}`,
      ratio,
      safeZones: [
        {
          id: `${assetType}-overlay`,
          imageSrc: SAFE_ZONE_OVERLAY_BY_ASSET_TYPE[assetType],
          ...getSafeZoneGeometryForAssetType(assetType),
        },
      ],
    };

    return presets;
  },
  {}
);

const STORY_WEB_PRESET_ID = 'safe-zone-story-web';
const STORY_COMBINED_PRESET_ID = 'safe-zone-story-combined';
const STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID = 'safe-zone-storyThumbnail-chip-left';
const CLIP_WEB_PRESET_ID = 'safe-zone-clip-web';
const CLIP_COMBINED_PRESET_ID = 'safe-zone-clip-combined';
const CLIP_AD_PRESET_ID = 'safe-zone-clip-ad';
const CLIP_AD_WEB_PRESET_ID = 'safe-zone-clip-ad-web';
const CLIP_AD_COMBINED_PRESET_ID = 'safe-zone-clip-ad-combined';
const CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID =
  'safe-zone-clipThumbnail-new-chip-left';
const AD_WEB_PRESET_ID = 'safe-zone-ad-web';
const AD_COMBINED_PRESET_ID = 'safe-zone-ad-combined';
const CARD_9X16_PRESET_ID = 'safe-zone-card-9x16';
const CARD_2X3_PRESET_ID = 'safe-zone-card-2x3';
const CARD_1X1_PRESET_ID = 'safe-zone-card-1x1';
const CARD_16X9_PRESET_ID = 'safe-zone-card-16x9';
const CARD_4X5_PRESET_ID = 'safe-zone-card-4x5';

const THUMBNAIL_CHIP_DIRECTIONS = {
  LEFT: 'left',
  RIGHT: 'right',
};

function normalizeThumbnailChipDirection(thumbnailChipDirection) {
  const normalizedDirection = String(thumbnailChipDirection || '')
    .trim()
    .toLowerCase();

  if (normalizedDirection.includes(THUMBNAIL_CHIP_DIRECTIONS.LEFT)) {
    return THUMBNAIL_CHIP_DIRECTIONS.LEFT;
  }

  return THUMBNAIL_CHIP_DIRECTIONS.RIGHT;
}

function mapThumbnailPresetByDirection(presetId, thumbnailChipDirection) {
  const normalizedDirection = normalizeThumbnailChipDirection(
    thumbnailChipDirection
  );

  if (normalizedDirection === THUMBNAIL_CHIP_DIRECTIONS.LEFT) {
    if (presetId === PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail]) {
      return STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID;
    }
    if (presetId === PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail]) {
      return CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID;
    }
  }

  if (normalizedDirection === THUMBNAIL_CHIP_DIRECTIONS.RIGHT) {
    if (presetId === STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID) {
      return PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail];
    }
    if (presetId === CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID) {
      return PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail];
    }
  }

  return presetId;
}

function applyThumbnailChipDirectionToDefaultPresets(
  presetIds = [],
  thumbnailChipDirection
) {
  return presetIds.map((presetId) =>
    mapThumbnailPresetByDirection(presetId, thumbnailChipDirection)
  );
}

const CUSTOM_SAFE_ZONE_PRESETS = {
  [STORY_WEB_PRESET_ID]: {
    id: STORY_WEB_PRESET_ID,
    label: 'Story (Web)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'story-web-overlay',
        imageSrc: storyWebOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [STORY_COMBINED_PRESET_ID]: {
    id: STORY_COMBINED_PRESET_ID,
    label: 'Story',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'story-combined-overlay',
        imageSrc: storyCombinedOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID]: {
    id: STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID,
    label: 'Story Thumbnail (Chip Left)',
    ratio: 2 / 3,
    safeZones: [
      {
        id: 'storyThumbnail-chip-left-overlay',
        imageSrc: storyThumbnailChipLeftOverlay,
        ...getSafeZoneGeometryForRatio(2 / 3),
      },
    ],
  },
  [CLIP_WEB_PRESET_ID]: {
    id: CLIP_WEB_PRESET_ID,
    label: 'Clip (Web)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'clip-web-overlay',
        imageSrc: clipWebOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CLIP_COMBINED_PRESET_ID]: {
    id: CLIP_COMBINED_PRESET_ID,
    label: 'Clip',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'clip-combined-overlay',
        imageSrc: clipCombinedOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CLIP_AD_PRESET_ID]: {
    id: CLIP_AD_PRESET_ID,
    label: 'Clip Ad (Mobile)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'clip-ad-overlay',
        imageSrc: clipAdMobileOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CLIP_AD_WEB_PRESET_ID]: {
    id: CLIP_AD_WEB_PRESET_ID,
    label: 'Clip Ad (Web)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'clip-ad-web-overlay',
        imageSrc: clipAdWebOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CLIP_AD_COMBINED_PRESET_ID]: {
    id: CLIP_AD_COMBINED_PRESET_ID,
    label: 'Clip Ad',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'clip-ad-combined-overlay',
        imageSrc: clipAdCombinedOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID]: {
    id: CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID,
    label: 'Clip Thumbnail (Chip Left)',
    ratio: 2 / 3,
    safeZones: [
      {
        id: 'clipThumbnail-new-chip-left-overlay',
        imageSrc: storyThumbnailChipLeftOverlay,
        ...getSafeZoneGeometryForRatio(2 / 3),
      },
    ],
  },
  [AD_WEB_PRESET_ID]: {
    id: AD_WEB_PRESET_ID,
    label: 'Story Ad (Web)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'ad-web-overlay',
        imageSrc: adWebOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [AD_COMBINED_PRESET_ID]: {
    id: AD_COMBINED_PRESET_ID,
    label: 'Story Ad',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'ad-combined-overlay',
        imageSrc: adCombinedOverlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CARD_9X16_PRESET_ID]: {
    id: CARD_9X16_PRESET_ID,
    label: 'Cards Asset (9:16)',
    ratio: 9 / 16,
    safeZones: [
      {
        id: 'cardAsset-9x16-overlay',
        imageSrc: cardAsset9x16Overlay,
        ...getSafeZoneGeometryForRatio(9 / 16),
      },
    ],
  },
  [CARD_2X3_PRESET_ID]: {
    id: CARD_2X3_PRESET_ID,
    label: 'Cards Asset (2:3)',
    ratio: 2 / 3,
    safeZones: [
      {
        id: 'cardAsset-2x3-overlay',
        imageSrc: cardAsset2x3Overlay,
        ...getSafeZoneGeometryForRatio(2 / 3),
      },
    ],
  },
  [CARD_1X1_PRESET_ID]: {
    id: CARD_1X1_PRESET_ID,
    label: 'Cards Asset (1:1)',
    ratio: 1,
    safeZones: [
      {
        id: 'cardAsset-1x1-overlay',
        imageSrc: cardAsset1x1Overlay,
        ...getSafeZoneGeometryForRatio(1),
      },
    ],
  },
  [CARD_16X9_PRESET_ID]: {
    id: CARD_16X9_PRESET_ID,
    label: 'Cards Asset (16:9)',
    ratio: 16 / 9,
    safeZones: [
      {
        id: 'cardAsset-16x9-overlay',
        imageSrc: cardAsset16x9Overlay,
        ...getSafeZoneGeometryForRatio(16 / 9),
      },
    ],
  },
  [CARD_4X5_PRESET_ID]: {
    id: CARD_4X5_PRESET_ID,
    label: 'Cards Asset (4:5)',
    ratio: 4 / 5,
    safeZones: [
      {
        id: 'cardAsset-4x5-overlay',
        imageSrc: cardAsset4x5Overlay,
        ...getSafeZoneGeometryForRatio(4 / 5),
      },
    ],
  },
};

export const SAFE_ZONE_PRESETS = {
  ...BASE_SAFE_ZONE_PRESETS,
  ...CUSTOM_SAFE_ZONE_PRESETS,
};

const ALL_PRESET_IDS_BY_RATIO_KEY = {
  [getRatioKey(9 / 16)]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Story],
    STORY_WEB_PRESET_ID,
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Clip],
    CLIP_WEB_PRESET_ID,
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Ad],
    AD_WEB_PRESET_ID,
    CLIP_AD_PRESET_ID,
    CLIP_AD_WEB_PRESET_ID,
    CARD_9X16_PRESET_ID,
  ],
  [getRatioKey(2 / 3)]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail],
    STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID,
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail],
    CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID,
    CARD_2X3_PRESET_ID,
  ],
  [getRatioKey(1)]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryIcon],
    CARD_1X1_PRESET_ID,
  ],
  [getRatioKey(16 / 9)]: [CARD_16X9_PRESET_ID],
  [getRatioKey(4 / 5)]: [CARD_4X5_PRESET_ID],
};

const DEFAULT_PRESET_IDS_BY_RATIO_KEY = {
  [getRatioKey(9 / 16)]: [
    STORY_COMBINED_PRESET_ID,
    CLIP_COMBINED_PRESET_ID,
    AD_COMBINED_PRESET_ID,
    CLIP_AD_COMBINED_PRESET_ID,
    CARD_9X16_PRESET_ID,
  ],
  [getRatioKey(2 / 3)]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail],
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail],
    CARD_2X3_PRESET_ID,
  ],
  [getRatioKey(1)]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryIcon],
    CARD_1X1_PRESET_ID,
  ],
  [getRatioKey(16 / 9)]: [CARD_16X9_PRESET_ID],
  [getRatioKey(4 / 5)]: [CARD_4X5_PRESET_ID],
};

const EXPLICIT_PRESET_IDS_BY_ASSET_TYPE = {
  [AssetTypes.Story]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Story],
    STORY_WEB_PRESET_ID,
  ],
  [AssetTypes.StoryThumbnail]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail],
    STORY_THUMBNAIL_CHIP_LEFT_PRESET_ID,
  ],
  [AssetTypes.StoryIcon]: [PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryIcon]],
  [AssetTypes.Clip]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Clip],
    CLIP_WEB_PRESET_ID,
  ],
  [AssetTypes.ClipThumbnail]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail],
    CLIP_THUMBNAIL_NEW_CHIP_LEFT_PRESET_ID,
  ],
  [AssetTypes.Ad]: [
    PRESET_ID_BY_ASSET_TYPE[AssetTypes.Ad],
    AD_WEB_PRESET_ID,
    CLIP_AD_PRESET_ID,
    CLIP_AD_WEB_PRESET_ID,
  ],
  [AssetTypes.CardsAsset]: [CARD_1X1_PRESET_ID],
};

function getContentBankPresetIdsByAssetType(contentBankAssetType) {
  const ratioKey = getRatioKeyForAssetType(contentBankAssetType);
  if (!ratioKey) {
    return [];
  }

  return DEFAULT_PRESET_IDS_BY_RATIO_KEY[ratioKey] || [];
}

const CONTENT_BANK_SAFE_ZONE_PRESET_IDS_BY_TYPE = Object.values(
  AssetTypes
).reduce((map, contentBankAssetType) => {
  const presetIds = getContentBankPresetIdsByAssetType(contentBankAssetType);
  if (presetIds.length) {
    map[contentBankAssetType] = presetIds;
  }

  return map;
}, {});

const CARDS_PRESET_IDS_BY_RATIO_KEY = {
  [getRatioKey(9 / 16)]: [CARD_9X16_PRESET_ID],
  [getRatioKey(2 / 3)]: [CARD_2X3_PRESET_ID],
  [getRatioKey(1)]: [CARD_1X1_PRESET_ID],
  [getRatioKey(16 / 9)]: [CARD_16X9_PRESET_ID],
  [getRatioKey(4 / 5)]: [CARD_4X5_PRESET_ID],
};

function getCardsPresetIdsByAssetType(cardsAssetType) {
  const ratioKey = getRatioKeyForAssetType(cardsAssetType);
  if (!ratioKey) {
    return [];
  }

  return CARDS_PRESET_IDS_BY_RATIO_KEY[ratioKey] || [];
}

const CARDS_ASSET_SAFE_ZONE_PRESET_IDS_BY_TYPE = Object.values(
  AssetTypes
).reduce((map, cardsAssetType) => {
  const presetIds = getCardsPresetIdsByAssetType(cardsAssetType);
  if (presetIds.length) {
    map[cardsAssetType] = presetIds;
  }

  return map;
}, {});

const DEFAULT_PRESET_IDS_BY_ASSET_TYPE = {
  [AssetTypes.Story]: [STORY_COMBINED_PRESET_ID],
  [AssetTypes.StoryThumbnail]: [PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryThumbnail]],
  [AssetTypes.StoryIcon]: [PRESET_ID_BY_ASSET_TYPE[AssetTypes.StoryIcon]],
  [AssetTypes.Clip]: [CLIP_COMBINED_PRESET_ID],
  [AssetTypes.ClipThumbnail]: [PRESET_ID_BY_ASSET_TYPE[AssetTypes.ClipThumbnail]],
  [AssetTypes.Ad]: [AD_COMBINED_PRESET_ID, CLIP_AD_COMBINED_PRESET_ID],
  [AssetTypes.ContentBankAsset]: [],
  [AssetTypes.CardsAsset]: [CARD_1X1_PRESET_ID],
  [AssetTypes.Template]: [],
};

const BASE_SAFE_ZONE_REGISTRY = Object.values(AssetTypes).reduce(
  (registry, assetType) => {
    registry[assetType] = {
      defaultPresetIds: DEFAULT_PRESET_IDS_BY_ASSET_TYPE[assetType] || [],
      byPageType: {},
    };

    return registry;
  },
  {}
);

export const SAFE_ZONE_REGISTRY = {
  ...BASE_SAFE_ZONE_REGISTRY,
  [AssetTypes.ContentBankAsset]: {
    ...BASE_SAFE_ZONE_REGISTRY[AssetTypes.ContentBankAsset],
    byContentBankAssetType: CONTENT_BANK_SAFE_ZONE_PRESET_IDS_BY_TYPE,
  },
  [AssetTypes.CardsAsset]: {
    ...BASE_SAFE_ZONE_REGISTRY[AssetTypes.CardsAsset],
    byContentBankAssetType: CARDS_ASSET_SAFE_ZONE_PRESET_IDS_BY_TYPE,
  },
};

function getPresetIdsFromDefinition(
  definition,
  cmsPageType,
  contentBankAssetType
) {
  if (!definition) {
    return [];
  }

  const pageTypePresetIds = definition.byPageType?.[cmsPageType];

  if (pageTypePresetIds?.length) {
    return pageTypePresetIds;
  }

  const contentBankPresetIds =
    definition.byContentBankAssetType?.[contentBankAssetType];

  if (contentBankPresetIds?.length) {
    return contentBankPresetIds;
  }

  // If a subtype was explicitly provided for a subtype-driven registry (content
  // bank/cards) and we don't have presets for that subtype ratio, do not fall
  // back to defaults from another ratio.
  if (
    contentBankAssetType &&
    definition.byContentBankAssetType &&
    !Object.prototype.hasOwnProperty.call(
      definition.byContentBankAssetType,
      contentBankAssetType
    )
  ) {
    return [];
  }

  return definition.defaultPresetIds || [];
}

function getRegisteredPresetIds({
  assetType,
  cmsPageType,
  contentBankAssetType,
  thumbnailChipDirection,
}) {
  if (!assetType) {
    return [];
  }

  const definition = SAFE_ZONE_REGISTRY[assetType];
  const presetIds = getPresetIdsFromDefinition(
    definition,
    cmsPageType,
    contentBankAssetType
  );

  return applyThumbnailChipDirectionToDefaultPresets(
    presetIds,
    thumbnailChipDirection
  );
}

function getPresetById(presetId) {
  if (!presetId) {
    return null;
  }

  return SAFE_ZONE_PRESETS[presetId] || null;
}

function createPresetOption(presetId) {
  const preset = getPresetById(presetId);
  if (!preset) {
    return null;
  }

  return {
    label: preset.label,
    value: preset.id,
  };
}

function getPresetIdsByRatioAssetType(
  ratioAssetType,
  defaultOnlyByRatio = false,
  thumbnailChipDirection
) {
  const ratioKey = getRatioKeyForAssetType(ratioAssetType);
  if (!ratioKey) {
    return [];
  }

  const presetIdsByRatio = defaultOnlyByRatio
    ? DEFAULT_PRESET_IDS_BY_RATIO_KEY
    : ALL_PRESET_IDS_BY_RATIO_KEY;

  const presetIds = presetIdsByRatio[ratioKey] || [];

  if (defaultOnlyByRatio) {
    return applyThumbnailChipDirectionToDefaultPresets(
      presetIds,
      thumbnailChipDirection
    );
  }

  return presetIds;
}

export function getSafeZonePresetOptions({
  assetType,
  cmsPageType,
  contentBankAssetType,
  thumbnailChipDirection,
  includeAllPresets = false,
  ratioAssetType,
  defaultOnlyByRatio = false,
} = {}) {
  const presetIds = includeAllPresets
    ? ratioAssetType
      ? getPresetIdsByRatioAssetType(
          ratioAssetType,
          defaultOnlyByRatio,
          thumbnailChipDirection
        )
      : Object.keys(SAFE_ZONE_PRESETS)
    : getRegisteredPresetIds({
        assetType,
        cmsPageType,
        contentBankAssetType,
        thumbnailChipDirection,
      });

  return Array.from(new Set(presetIds))
    .map(createPresetOption)
    .filter(Boolean);
}

function getSafeZonePresetIdsForAssignedAssetType(
  assetType,
  { useDefaultPresets = false, thumbnailChipDirection } = {}
) {
  const explicitPresetIds = EXPLICIT_PRESET_IDS_BY_ASSET_TYPE[assetType];

  if (explicitPresetIds) {
    return useDefaultPresets
      ? getRegisteredPresetIds({
          assetType,
          thumbnailChipDirection,
        })
      : explicitPresetIds;
  }

  return getPresetIdsByRatioAssetType(
    assetType,
    useDefaultPresets,
    thumbnailChipDirection
  );
}

export function getSafeZonePresetIdsForAssetTypes(
  assetTypes = [],
  options = {}
) {
  if (!Array.isArray(assetTypes)) {
    return [];
  }

  return Array.from(
    new Set(
      assetTypes.flatMap((assetType) =>
        getSafeZonePresetIdsForAssignedAssetType(assetType, options)
      )
    )
  );
}

export function getSafeZonePresetOptionsForAssetTypes(
  assetTypes = [],
  options = {}
) {
  return getSafeZonePresetIdsForAssetTypes(assetTypes, options)
    .map(createPresetOption)
    .filter(Boolean);
}

export function getSafeZonesForAsset({
  assetType,
  cmsPageType,
  contentBankAssetType,
  thumbnailChipDirection,
  safeZonePresetId,
  allowPresetOverride = false,
} = {}) {
  if (!assetType) {
    return [];
  }

  if (allowPresetOverride) {
    if (safeZonePresetId === NOT_SET_SAFE_ZONE_PRESET_ID) {
      return [];
    }

    const forcedPreset = getPresetById(safeZonePresetId);
    if (forcedPreset) {
      return forcedPreset.safeZones || [];
    }
  }

  const registeredPresetIds = getRegisteredPresetIds({
    assetType,
    cmsPageType,
    contentBankAssetType,
    thumbnailChipDirection,
  });
  const defaultPreset = registeredPresetIds.map(getPresetById).find(Boolean);

  return defaultPreset?.safeZones || [];
}
